import React, { FC, useCallback } from 'react';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { Box, MenuItem, Typography } from '@mui/material';
import { CoreModal, CoreModalProps } from '../../../_core/components/_ui/core-modal';
import { CoreButton } from '../../../_core/components/_ui/core-button';
import { useTranslation } from 'react-i18next';
import { containerButtonStyles, errorTextStyles, leftButtonStyles, modalBoxStyles } from './modal-form.styles';
import { buttonModalWindowStyles, mainTitleModalWindowStyles } from '../../../_core/styles';
import { mergeSx } from 'merge-sx';
import { CoreTextField } from '../../../_core/components/_ui/core-textfield';
import { AutocompleteTextField, CoreAutoComplete } from '../../../_core/components/_ui/core-autocomplete';
import { LanguageEnum } from '@families-link/shared';
import { getObjectLanguage } from '../../../_core/components/languages-component';

export type CreateSurveyTranslationProps = CoreModalProps & {
	languageList?: Array<string>;
	formError?: any;
	submitAction(values: { language: string }): Promise<void>;
	closeModal(): void;
};

export const CreateSurveyTranslation: FC<CreateSurveyTranslationProps> = (props) => {
	const [t] = useTranslation();
	const { submitAction, closeModal, languageList, formError, ...modalProps } = props;
	const defaultValues: { language?: string } = { language: '' };
	const {
		register,
		control,
		handleSubmit,
		getValues,
		formState: { isDirty, isLoading, isSubmitting, isSubmitted, isValid, errors },
	} = useForm({
		defaultValues,
		mode: 'all',
	});

	const disableSubmit = !isValid && (isDirty || isSubmitted);
	const loadingSubmit = isLoading || isSubmitting;

	const handleFormSubmit = useCallback(
		async (values: any) => {
			try {
				await submitAction(values);
			} catch (error) {
				console.error(error);
			}
		},
		[submitAction]
	);

	return (
		<CoreModal {...modalProps} modalWidth={500} bodySx={{ pt: 0 }} paperSx={{ width: '100%' }}>
			<Typography variant="h5" sx={mainTitleModalWindowStyles}>
				{t('survey-translations.forms.create-survey-translation')}
			</Typography>
			<Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
				<CoreAutoComplete
					label={t('engagements.forms.labels.language')}
					requiredMark
					fullWidth
					{...register('language')}
					control={control}
					ruleIsRequired
					ruleName={t('engagements.forms.labels.language')}
					error={!!errors['language']}
					helperText={(errors['language'] as FieldError)?.message}
					options={Object.values(LanguageEnum)}
					getOptionLabel={(option) => {
						const language = getObjectLanguage(option);

						return language ? language.name : '';
					}}
					selectOnFocus={true}
					isOptionEqualToValue={(option, value) => option === value}
					disableClearable={!getValues('language')}
					renderInput={(params) => (
						<AutocompleteTextField {...params} error={!!errors['language']} placeholder={'Select a language'} />
					)}
					renderOption={(props, option) => {
						const language = getObjectLanguage(option);

						return (
							<MenuItem {...props} key={language?.value}>
								{language?.label}
							</MenuItem>
						);
					}}
				/>
				{formError && (
					<Typography sx={errorTextStyles}>
						{formError?.data?.message ? formError.data.message : t('errors.invalid-credentials-error')}
					</Typography>
				)}
				<Box sx={mergeSx(containerButtonStyles, { mt: 5 })}>
					<CoreButton onClick={closeModal} variant="secondary" sx={mergeSx(buttonModalWindowStyles, leftButtonStyles)}>
						{t('survey-templates.forms.buttons.close-form')}
					</CoreButton>
					<CoreButton
						type="submit"
						variant="info-light"
						contraModeStyleButton
						disabled={disableSubmit}
						loading={loadingSubmit}
						sx={buttonModalWindowStyles}
					>
						{t('survey-templates.forms.buttons.create')}
					</CoreButton>
				</Box>
			</Box>
		</CoreModal>
	);
};
